import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";

import { deleteFile, uploadFile } from "api";
import errorToast from "helpers/errorToast";
import useDraggableUpload from "./useDraggableUpload";

const updateFileState = (file: CustomFileType, update: Partial<CustomFileType>) => (prevFiles: CustomFileType[]) =>
  prevFiles.map((prevFile) => (prevFile.id === file.id ? { ...prevFile, ...update } : prevFile));

export type CustomFileType = {
  // Actually file binary
  file: File;

  // Data returned from the backend
  reference_number?: string;
  url?: string;
  size?: number;

  // Custom properties
  id: number;
  isError?: boolean;
  errorMessage?: string;
  isUploaded?: boolean;
  isUploading?: boolean;
  isDeleting?: boolean;
};

interface useFileHookProps {
  maxFileCount?: number;
}

export default function useFileHook({ maxFileCount = 10 }: useFileHookProps) {
  const intl = useIntl();

  const [files, setFiles] = useState<CustomFileType[]>([]);
  const formattedFiles = files.map((f) => ({
    filename: f.file.name,
    size: f.file.size,
    url: f.url,
    reference_number: f.reference_number,
  }));

  const idCounter = useRef(0);
  const hiddenFileInputRef = useRef<HTMLInputElement>(null);

  const resetFiles = () => setFiles([]);

  const validateFile = (file: File): CustomFileType => {
    const id = idCounter.current;
    idCounter.current += 1;

    // Check if file is larger than 15 MegaBytes
    if (file.size > 15_000_000) {
      return {
        file,
        id,
        isError: true,
        errorMessage: intl.formatMessage({
          id: "general.fileSizeExceedsError",
          defaultMessage: "Upload failed, size exceeds 15MB.",
        }),
      };
    }
    return { file, id, isUploading: true };
  };

  const processFile = (file: CustomFileType) => {
    const formData = new FormData();
    formData.append("file", file.file);
    formData.append("filename", file.file.name);
    formData.append("purpose", "comment_attachment");

    uploadFile(formData)
      .then((response) => {
        setFiles(
          updateFileState(file, {
            isUploaded: true,
            isUploading: false,
            reference_number: response.data.reference_number,
            url: response.data.url,
            size: response.data.size,
          }),
        );
      })
      .catch((error) => {
        if (error.response?.status === 0) {
          setFiles(
            updateFileState(file, {
              isError: true,
              isUploading: false,
              errorMessage: "Network error",
            }),
          );
          return;
        }
        const errorData = error.response.data;
        const errorMessage =
          errorData?.message ??
          errorData?.detail ??
          (Array.isArray(errorData?.attachment) ? errorData?.attachment?.[0] : "");
        setFiles(
          updateFileState(file, {
            isError: true,
            isUploading: false,
            errorMessage,
          }),
        );
      });
  };

  const handleFileSelect = (inputFiles: FileList | null) => {
    if (!inputFiles) {
      return;
    }
    if (files.length + inputFiles.length > maxFileCount) {
      errorToast(
        null,
        intl.formatMessage({
          id: "tracking.activityField.uploadFailMaxFileCount",
          defaultMessage: "Upload failed. You may only upload 10 files at a time",
        }),
      );
      return;
    }
    const validatedFiles = Array.from(inputFiles).map(validateFile);
    setFiles((prevFiles) => [...prevFiles, ...validatedFiles]);

    validatedFiles.forEach((file, index) => {
      if (!file.isError) {
        setTimeout(() => {
          processFile(file);
        }, index * 1000);
      }
    });
  };

  const handleRemoveFile = (file: CustomFileType) => {
    // If file has been uploaded to the server, delete it
    if (file.reference_number) {
      setFiles(
        updateFileState(file, {
          isDeleting: true,
        }),
      );
      deleteFile(file.reference_number).then(() => {
        setFiles((prevFiles) => prevFiles.filter((prevFile) => prevFile.id !== file.id));
      });
    }
    // This only happens if the file has not been uploaded (File has err from FE validation)
    else {
      setFiles((prevFiles) => prevFiles.filter((prevFile) => prevFile.id !== file.id));
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return;
    handleFileSelect(event.target.files);
  };

  // We want to prevent pasting the filename into the textarea if the user pastes a file or files
  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const clipboardItems = e.clipboardData.items;

    for (let i = 0; i < clipboardItems.length; i += 1) {
      const item = clipboardItems[i];
      if (item.kind === "file") {
        e.preventDefault(); // Prevent the default paste action
        return;
      }
    }
  };

  const { dragging, ...draggableProps } = useDraggableUpload(handleFileSelect);

  // If user pastes an image, we want to upload it
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      if (hiddenFileInputRef?.current) {
        if (e.clipboardData?.files) {
          handleFileSelect(e.clipboardData.files);
        }
      }
    };
    window.addEventListener("paste", handlePaste as EventListener);
    return () => {
      window.removeEventListener("paste", handlePaste as EventListener);
    };
  }, [files]);

  const filesSortedByErrorsFirst = [...files].sort((a, b) => {
    if (a.isError && !b.isError) return -1;
    if (!a.isError && b.isError) return 1;
    return 0;
  });

  return {
    files,
    formattedFiles,
    filesSortedByErrorsFirst,
    resetFiles,
    dragging,
    draggableProps,
    handlePaste,
    handleRemoveFile,
    handleFileUpload,
    hiddenFileInputRef,
  };
}
